import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import LearningPathway from "../components/LearningPathway";
import { api, fetchSitePrograms } from "../lib/api";
import { trackOnce } from "../lib/analytics";
import Honeypot from "../components/Honeypot";

const STEPS = [
  { key: "age", label: "Child's age" },
  { key: "recommend", label: "Recommendation" },
  { key: "review", label: "Program details" },
  { key: "book", label: "Book trial" },
];

const AGE_BUCKETS = [
  { label: "4–6 years", min: 4, max: 6 },
  { label: "7–9 years", min: 7, max: 9 },
  { label: "10–12 years", min: 10, max: 12 },
  { label: "13–17 years", min: 13, max: 17 },
];

const SUGGESTED_INTERESTS = [
  "Building",
  "Robotics",
  "Coding",
  "STEM",
  "Bricks",
  "Sports",
];

// Interest synonyms so chips match program tags/names loosely
// (e.g. "Building" should surface Brick-Based & STEM Engineering).
const INTEREST_SYNONYMS = {
  building: ["building", "bricks", "brick", "engineering", "stem"],
  bricks: ["bricks", "brick", "building", "engineering"],
  stem: ["stem", "engineering", "building", "robotics", "problem solving"],
  robotics: ["robotics", "robot", "coding", "stem"],
  coding: ["coding", "code", "robotics", "stem"],
  sports: ["sports", "tennis", "movement"],
};

// Program age range: prefer numeric fields, fall back to parsing the display
// text (handles both en dash "7–9" and hyphen "7-9").
function programAges(program) {
  let min = program.age_min;
  let max = program.age_max;
  if (min == null || max == null) {
    const m = String(program.age_range || program.ages || "").match(/(\d+)\s*[–—-]\s*(\d+)/);
    if (m) {
      if (min == null) min = parseInt(m[1], 10);
      if (max == null) max = parseInt(m[2], 10);
    }
  }
  return { min: min ?? 4, max: max ?? 17 };
}

// Trial funnel priority: regular classes first, camps second, support last.
const TYPE_PRIORITY = { term: 3, "": 2, camp: 2, play_based_support: 1 };

function scoreFor(program, ageBucket, interests) {
  if (program.active === false) return null;
  if (program.program_type === "waitlist") return null; // coming soon — no trials yet
  const { min, max } = programAges(program);
  const overlaps = ageBucket.min <= max && ageBucket.max >= min;
  const near = ageBucket.min <= max + 1 && ageBucket.max >= min - 1;
  if (!overlaps && !near) return null;

  let score = overlaps ? 5 : 2;
  const tags = (program.interests || []).map((x) => String(x).toLowerCase());
  const text = `${program.name} ${program.category || ""}`.toLowerCase();
  const matched = [];
  interests.forEach((i) => {
    const key = i.toLowerCase();
    // Direct match on the interest itself outranks synonym matches so e.g.
    // Robotics & Coding beats STEM Engineering for a robotics-only parent.
    if (tags.includes(key) || text.includes(key)) {
      score += 5;
      matched.push(i);
      return;
    }
    const syns = INTEREST_SYNONYMS[key] || [];
    if (syns.some((s) => tags.includes(s) || text.includes(s))) {
      score += 3;
      matched.push(i);
    }
  });
  score += TYPE_PRIORITY[program.program_type ?? ""] ?? 2;

  let reason;
  if (overlaps && matched.length) {
    reason = `Recommended because it matches your child's age (${ageBucket.label}) and interest in ${matched.join(" / ")}.`;
  } else if (overlaps) {
    reason = `A great fit for the ${ageBucket.label} age group.`;
  } else {
    reason = `Close to your child's age group — worth a look.`;
  }
  return { score, reason, matchedCount: matched.length };
}

export default function TrialClassFunnelPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const preselectSlug = location.state?.programSlug;

  const [step, setStep] = useState(0);
  const [programs, setPrograms] = useState([]);
  const [ageBucket, setAgeBucket] = useState(null);
  const [interests, setInterests] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(preselectSlug || "");
  const [form, setForm] = useState({
    parent_name: "",
    email: "",
    phone: "",
    child_name: "",
    child_age: "",
    preferred_schedule: "",
    message: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const loadPrograms = () => {
    setLoadError(false);
    fetchSitePrograms()
      .then(setPrograms)
      .catch((err) => {
        setLoadError(true);
        if (process.env.NODE_ENV === "development") {
          console.warn("Trial funnel: failed to load programs", err?.message);
        }
      });
  };

  useEffect(() => {
    loadPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If a program was pre-selected from a landing page, jump to step 2 (Review).
  useEffect(() => {
    if (preselectSlug && programs.length) {
      const p = programs.find((x) => x.slug === preselectSlug);
      if (p) {
        setAgeBucket(
          AGE_BUCKETS.find((b) => (p.age_min ?? 4) <= b.max && (p.age_max ?? 17) >= b.min) ||
            AGE_BUCKETS[1]
        );
        setSelectedSlug(p.slug);
        setStep(2);
      }
    }
  }, [preselectSlug, programs]);

  const recommendations = useMemo(() => {
    if (!ageBucket) return { items: [], fallback: false };
    const scored = programs
      .map((p) => {
        const r = scoreFor(p, ageBucket, interests);
        return r ? { p, ...r } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    if (scored.length) {
      // If the parent picked interests we don't directly serve (e.g. Sports),
      // still show age-appropriate cards but flag it as a soft match.
      const anyInterestMatched = scored.some((x) => x.matchedCount > 0);
      return {
        items: scored.slice(0, 3),
        fallback: interests.length > 0 && !anyInterestMatched,
      };
    }
    // Fallback: never show an empty result while active programs exist.
    const general = programs
      .filter((p) => p.active !== false && p.program_type !== "waitlist")
      .slice(0, 3)
      .map((p) => ({
        p,
        reason: "A popular starting point — we'll help confirm the best fit.",
      }));
    return { items: general, fallback: general.length > 0 };
  }, [programs, ageBucket, interests]);

  const selectedProgram = useMemo(
    () => programs.find((p) => p.slug === selectedSlug),
    [programs, selectedSlug]
  );

  const selectedReason = useMemo(
    () => recommendations.items.find((x) => x.p.slug === selectedSlug)?.reason,
    [recommendations, selectedSlug]
  );

  // Prefill an editable, parent-friendly summary when reaching the booking step.
  useEffect(() => {
    if (step === 3 && !form.message && selectedProgram) {
      const parts = ["I am interested in booking a trial class."];
      if (ageBucket) {
        parts.push(
          `My child is in the ${ageBucket.label} age range${
            interests.length ? ` and is interested in ${interests.join(", ")}` : ""
          }.`
        );
      }
      parts.push(`Recommended program: ${selectedProgram.name}.`);
      setForm((f) => ({ ...f, message: parts.join(" ") }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const toggleInterest = (val) =>
    setInterests((arr) => (arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]));

  const submit = async () => {
    if (!form.parent_name || !form.email || !form.phone) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        parent_name: form.parent_name,
        email: form.email,
        phone: form.phone,
        child_name: form.child_name || undefined,
        child_age: form.child_age || (ageBucket ? ageBucket.label : undefined),
        program_slug: selectedProgram?.slug || "play-based-support",
        program_type: selectedProgram?.program_type === "camp" ? "camp" : "program",
        preferred_schedule: form.preferred_schedule || undefined,
        message: form.message || undefined,
        inquiry_type: "trial_class",
        selected_age_range: ageBucket?.label || undefined,
        selected_interests: interests,
        selected_program_name: selectedProgram?.name || undefined,
        recommendation_reason: selectedReason || undefined,
        source_page: "/trial-class",
        website: form.website,
      });
      setDone(true);
      toast.success("Trial request received — we'll be in touch.");
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---- Render ----
  return (
    <>
      <SEO
        path="/trial-class"
        title="Book a Trial Class | Nexora Richmond BC"
        description="Book a trial class at Nexora — STEM, Brick-Based Engineering, Robotics, Coding, and Summer Camps for kids in Richmond BC. Tell us your child's age and we'll recommend the right fit."
      />

      <section className="bg-[#FFFCF7] border-b border-slate-200/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
          <span className="pill bg-white border border-slate-200 text-slate-700 shadow-sm">
            <Sparkles className="h-3 w-3 text-[#FF8C42]" />
            Trial class funnel
          </span>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-extrabold text-slate-900 mt-5 leading-[1.06]">
            Book a trial class for your child.
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">
            Four quick steps. Tell us your child's age, see our top
            recommendation, then book a trial.
          </p>

          {/* Stepper */}
          <ol className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="trial-stepper">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                data-testid={`trial-step-indicator-${i}`}
                className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm ${
                  i === step
                    ? "bg-slate-900 text-white border-slate-900"
                    : i < step
                    ? "bg-sky-50 text-[#0369A1] border-[#0284C7]/30"
                    : "bg-white text-slate-500 border-slate-200"
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-bold text-xs ${
                    i === step
                      ? "bg-white text-slate-900"
                      : i < step
                      ? "bg-[#0284C7] text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {i < step ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="font-semibold">{s.label}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {done ? (
          <div
            className="rounded-[2rem] bg-white border border-slate-200/70 p-10 text-center shadow-sm"
            data-testid="trial-success"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-[#0369A1]">
              <Check className="h-6 w-6" />
            </span>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl font-extrabold text-slate-900">
              Trial request received.
            </h2>
            <p className="mt-3 max-w-lg mx-auto text-slate-600 leading-relaxed">
              A Nexora coordinator will follow up within one business day to
              confirm your trial class for {selectedProgram?.name || "Nexora"}.
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              <Link to="/" className="btn-soft">Back to home</Link>
              <Link to="/programs" className="btn-primary">
                Explore other programs
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="rounded-[2rem] bg-white border border-slate-200/70 p-7 sm:p-10 shadow-sm">
            {/* Step 1 — Age */}
            {step === 0 && (
              <div data-testid="trial-step-age">
                <p className="eyebrow">Step 1</p>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2">
                  How old is your child?
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  We'll use this to recommend programs that fit their age.
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {AGE_BUCKETS.map((b) => (
                    <button
                      key={b.label}
                      onClick={() => setAgeBucket(b)}
                      className={`rounded-2xl border p-5 text-left transition-colors ${
                        ageBucket?.label === b.label
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`trial-age-${b.label.replace(/[^a-z0-9]+/gi, "-")}`}
                    >
                      <p className="font-display font-bold">{b.label}</p>
                    </button>
                  ))}
                </div>

                <div className="mt-8">
                  <p className="eyebrow text-slate-600">Optional — what interests them?</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SUGGESTED_INTERESTS.map((i) => (
                      <button
                        key={i}
                        onClick={() => toggleInterest(i)}
                        className={`pill border transition-colors ${
                          interests.includes(i)
                            ? "bg-sky-50 text-[#0369A1] border-[#0284C7]"
                            : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                        }`}
                        data-testid={`trial-interest-${i.toLowerCase()}`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-10 flex justify-end">
                  <button
                    disabled={!ageBucket}
                    onClick={goNext}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="trial-step1-next"
                  >
                    See recommendation
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 — Recommendation list */}
            {step === 1 && (
              <div data-testid="trial-step-recommend">
                <p className="eyebrow">Step 2</p>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2">
                  Our top recommendations for {ageBucket?.label}
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Choose the program you'd like to trial.
                </p>

                <div className="mt-6 grid gap-3">
                  {recommendations.items.length === 0 && (
                    <div
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center"
                      data-testid="trial-no-programs"
                    >
                      <p className="text-slate-700 font-semibold">
                        We may be able to help — send us an inquiry and we'll
                        recommend the best fit for your child.
                      </p>
                      <div className="mt-4 flex justify-center gap-3 flex-wrap">
                        {loadError && (
                          <button onClick={loadPrograms} className="btn-soft" data-testid="trial-retry-programs">
                            Try again
                          </button>
                        )}
                        <Link to="/contact" className="btn-primary" data-testid="trial-fallback-contact">
                          Request Information
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  )}
                  {recommendations.fallback && (
                    <p className="text-slate-600 text-sm" data-testid="trial-fallback-note">
                      Here are a few good starting points. If you're not sure,
                      send us an inquiry and we'll help match your child to the
                      right program.
                    </p>
                  )}
                  {recommendations.items.map(({ p, reason }) => (
                    <button
                      key={p.slug}
                      onClick={() => setSelectedSlug(p.slug)}
                      className={`text-left rounded-2xl border p-5 transition-colors ${
                        selectedSlug === p.slug
                          ? "bg-sky-50 border-[#0284C7]"
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`trial-recommend-${p.slug}`}
                    >
                      <p className="font-display font-bold text-slate-900">{p.name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {p.age_range || p.ages || "All ages"} · {p.category}
                      </p>
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                        {p.short_description || p.description}
                      </p>
                      <p className="mt-2 text-xs font-semibold text-[#0369A1]" data-testid={`trial-reason-${p.slug}`}>
                        {reason}
                      </p>
                    </button>
                  ))}
                </div>

                <div className="mt-10 flex justify-between">
                  <button onClick={goBack} className="btn-soft" data-testid="trial-step2-back">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                  <button
                    disabled={!selectedSlug}
                    onClick={goNext}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="trial-step2-next"
                  >
                    Continue
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 — Review program */}
            {step === 2 && selectedProgram && (
              <div data-testid="trial-step-review">
                <p className="eyebrow">Step 3</p>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2">
                  Confirm: {selectedProgram.name}
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Quick recap of the program your child will trial.
                </p>

                <div className="mt-6 rounded-2xl border border-slate-200 p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`pill text-[10px] ${selectedProgram.accent || "bg-sky-50 text-[#0369A1]"}`}>
                      {selectedProgram.category}
                    </span>
                    <span className="pill bg-slate-100 text-slate-600 text-[10px]">
                      {selectedProgram.age_range || selectedProgram.ages}
                    </span>
                    <span className="pill bg-slate-100 text-slate-600 text-[10px]">
                      {selectedProgram.format || "In-person"}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-extrabold text-slate-900">
                    {selectedProgram.name}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {selectedProgram.short_description || selectedProgram.description}
                  </p>

                  {selectedProgram.highlights && (
                    <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                      {selectedProgram.highlights.slice(0, 4).map((h) => (
                        <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className="h-4 w-4 text-[#0369A1] shrink-0 mt-0.5" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link
                    to={`/programs/${selectedProgram.slug}`}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0369A1] hover:underline"
                    data-testid="trial-review-program-link"
                  >
                    See full program page
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>

                <div className="mt-10 flex justify-between">
                  <button onClick={goBack} className="btn-soft" data-testid="trial-step3-back">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                  <button
                    onClick={goNext}
                    className="btn-primary"
                    data-testid="trial-step3-next"
                  >
                    Book trial class
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 4 — Book */}
            {step === 3 && (
              <div data-testid="trial-step-book">
                <p className="eyebrow">Step 4</p>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2">
                  Tell us how to reach you.
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  We'll confirm your trial for {selectedProgram?.name} within one business day.
                </p>

                <div className="mt-6 grid gap-3.5">
                  <Honeypot value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <Field label="Parent / Guardian name *">
                      <input
                        required
                        className="input-soft"
                        value={form.parent_name}
                        onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                        onFocus={() =>
                          trackOnce("inquiry_started:/trial-class", "inquiry_started", {
                            source_page: "/trial-class",
                            program_slug: selectedProgram?.slug,
                          })
                        }
                        data-testid="trial-parent-name"
                      />
                    </Field>
                    <Field label="Phone *">
                      <input
                        required
                        type="tel"
                        className="input-soft"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="trial-phone"
                      />
                    </Field>
                  </div>
                  <Field label="Email *">
                    <input
                      required
                      type="email"
                      className="input-soft"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="trial-email"
                    />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <Field label="Child's name">
                      <input
                        className="input-soft"
                        value={form.child_name}
                        onChange={(e) => setForm({ ...form, child_name: e.target.value })}
                        data-testid="trial-child-name"
                      />
                    </Field>
                    <Field label="Preferred day / time">
                      <input
                        className="input-soft"
                        placeholder="e.g., Saturday mornings"
                        value={form.preferred_schedule}
                        onChange={(e) => setForm({ ...form, preferred_schedule: e.target.value })}
                        data-testid="trial-schedule"
                      />
                    </Field>
                  </div>
                  <Field label="Anything else we should know?">
                    <textarea
                      rows={3}
                      className="input-soft resize-none"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="trial-message"
                    />
                  </Field>
                </div>

                <div className="mt-10 flex justify-between flex-wrap gap-3">
                  <button onClick={goBack} className="btn-soft" data-testid="trial-step4-back">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                  <button
                    disabled={loading}
                    onClick={submit}
                    className="btn-primary disabled:opacity-50"
                    data-testid="trial-submit"
                  >
                    {loading ? "Sending…" : "Book my trial class"}
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Learning pathway — shown below the funnel to help parents understand the multi-year journey */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <LearningPathway
          highlight={new Set(["Brick Challenge", "Galileo Technic", "AlgoPlay", "RoboToys"])}
          title="The Young Engineers learning journey"
          subtitle="Whichever class your child starts in, they can keep growing through our multi-year pathway."
        />
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="eyebrow text-slate-600 block mb-2">{label}</label>
      {children}
    </div>
  );
}
